// Central mapping of sidebar/card badge keys to the notification catalogue.
// The AsideBar endpoints (Student/Counselor/Parent/Mentor) return unread notification
// counts as { notifications: { modules: {moduleKey: n}, definitions: {defKey: n} } }.
// A sidebar item or card that covers one whole module reads the module count; one that
// covers a subset of a module sums the relevant definition counts (handoff rule:
// "for any tab/card that covers multiple features, sum the relevant counts").
// Do NOT add a module count to one of its own definition counts (module already includes it).

// Read counts off the AsideBar response (tolerates the raw object or a nested notifications).
const notif = (badgeData) => badgeData?.notifications ?? badgeData ?? {}

export const moduleCount = (badgeData, moduleKey) => notif(badgeData).modules?.[moduleKey] ?? 0

export const definitionsCount = (badgeData, defKeys = []) => {
  const defs = notif(badgeData).definitions ?? {}
  return defKeys.reduce((total, key) => total + (defs[key] ?? 0), 0)
}

// Resolve a badge key against a role map to a single count.
// Map entry is { module: 'x' } (whole module) or { definitions: ['a','b'] } (summed subset).
export const badgeCountFor = (badgeData, key, roleMap) => {
  const entry = roleMap?.[key]
  if (!entry) return 0
  if (entry.module) return moduleCount(badgeData, entry.module)
  if (entry.definitions) return definitionsCount(badgeData, entry.definitions)
  return 0
}

// ---- STUDENT ----------------------------------------------------------------
// Student-audience definition keys grouped by the sidebar item / card that surfaces them.
export const STUDENT_BADGE_MAP = {
  // College Application card + sidebar (whole college-admissions module for this student).
  collegeApplication: { module: 'college-admissions' },
  // Notes.
  note: { module: 'notes' },
  // Reports card = all three assessments.
  reportCombined: { module: 'student-assessments' },
  careerPath: {
    definitions: [
      'student-assessments.career-path-published.recipient',
      'student-assessments.career-path-returned.student',
    ],
  },
  personalityTrait: {
    definitions: [
      'student-assessments.personality-assessment-completed.recipient',
      'student-assessments.personality-assessment-returned.student',
    ],
  },
  behaviorReport: {
    definitions: ['student-assessments.behavior-agreement-published.recipient'],
  },
  // Study Corner card = assignments + quizzes + notes.
  studyCornerCombined: {
    definitions: [
      'tutoring.assignment-assigned.student',
      'tutoring.assignment-marked.student',
      'tutoring.quiz-assigned.student',
      'tutoring.quiz-marked.student',
      'notes.note-added.student',
    ],
  },
  assignment: {
    definitions: ['tutoring.assignment-assigned.student', 'tutoring.assignment-marked.student'],
  },
  quiz: {
    definitions: ['tutoring.quiz-assigned.student', 'tutoring.quiz-marked.student'],
  },
  // Sessions card + tab (whole sessions module).
  sessionCombined: { module: 'sessions' },
  session: {
    definitions: [
      'sessions.tutor-session-scheduled.student',
      'sessions.counselor-session-scheduled.student',
    ],
  },
  counselorAccess: {
    definitions: [
      'sessions.counselor-access-requested.student',
      'sessions.counselor-access-approved.requester',
      'sessions.counselor-access-denied.requester',
    ],
  },
  wallet: {
    definitions: ['sessions.gift-granted.student', 'sessions.given-back.participant'],
  },
  // Recommendations (counselor session recommendations + recommended courses).
  recommendation: {
    definitions: [
      'sessions.recommendation-created.student',
      'sessions.recommendation-approved.student',
      'sessions.recommendation-declined.student',
      'tutoring.courses-recommended.student',
    ],
  },
  // Store card = commerce module.
  storeCombined: { module: 'commerce' },
  // Internship + Idea Factory.
  internship: { module: 'internships' },
  ideaFactory: { module: 'idea-factory' },
  // Messaging / chat.
  message: { definitions: ['chat.message-received.recipient'] },
  // Subscription and Calendar have no notification-catalogue counts (no badge).
}

const CHAT = { definitions: ['chat.message-received.recipient'] }

// ---- COUNSELOR --------------------------------------------------------------
export const COUNSELOR_BADGE_MAP = {
  collegeApplication: { module: 'college-admissions' },
  message: CHAT,
  newStudents: {
    definitions: [
      'actors.student-intake-completed.counselor',
      'actors.student-activated.counselor',
    ],
  },
  requestedSessions: { definitions: ['sessions.counselor-access-requested.counselor'] },
  sessions: { module: 'sessions' },
  // updateSchedule (availability) produces no notifications.
}

// ---- PARENT -----------------------------------------------------------------
export const PARENT_BADGE_MAP = {
  order: { module: 'commerce' },
  counselorAccessAndSession: { module: 'sessions' },
  session: { module: 'sessions' },
  message: CHAT,
  recommendation: { definitions: ['sessions.recommendation-created.parent'] },
  behaviorReport: { definitions: ['student-assessments.behavior-agreement-published.recipient'] },
  careerPath: { definitions: ['student-assessments.career-path-published.recipient'] },
  personalityTrait: {
    definitions: ['student-assessments.personality-assessment-completed.recipient'],
  },
  collegeApplication: { module: 'college-admissions' },
  note: { module: 'notes' },
  assignment: { module: 'tutoring' },
  quiz: { module: 'tutoring' },
}

// ---- MENTOR -----------------------------------------------------------------
export const MENTOR_BADGE_MAP = {
  assignedStudents: { definitions: ['internships.group-created.mentor'] },
  projectCombined: {
    definitions: [
      'internships.task-submitted.mentor',
      'internships.issue-reported.mentor',
      'internships.progress-report-submitted.mentor',
    ],
  },
  progressReport: { definitions: ['internships.progress-report-submitted.mentor'] },
  ideaFactory: { module: 'idea-factory' },
  message: CHAT,
  // projectLibrary, projectCertificate, projectCompleted, projectInflight, lessonLearned
  // have no mentor-audience notification definitions in the catalogue.
}
